import { useState, useEffect } from 'react';
import { X, Save, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { Prompt } from '@/types';
import { getPrompt, createPromptVersion, deployPromptVersion } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn, formatDate } from '@/lib/utils';

interface PromptEditorProps {
  agentId: string;
  promptName: string;
  version?: string;
  onClose: () => void;
  onUpdate?: () => void;
}

const MAX_CHARS = 2000;

export function PromptEditor({ agentId, promptName, version, onClose, onUpdate }: PromptEditorProps) {
  const [content, setContent] = useState('');
  const [currentPrompt, setCurrentPrompt] = useState<Prompt | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [versionNote, setVersionNote] = useState('');
  const [showVersionForm, setShowVersionForm] = useState(false);

  // Fetch prompt content
  useEffect(() => {
    const fetchPrompt = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getPrompt(agentId, promptName, version);
        setCurrentPrompt(data);
        setContent(data.content);
      } catch (err) {
        setError(err instanceof Error ? err.message : '載入 Prompt 失敗');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrompt();
  }, [agentId, promptName, version]);

  // Auto-save draft to localStorage
  useEffect(() => {
    const draftKey = `draft-${agentId}-${promptName}`;
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft && !content) {
      setContent(savedDraft);
    }

    const handleBeforeUnload = () => {
      localStorage.setItem(draftKey, content);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [content, agentId, promptName]);

  const charCount = content.length;
  const charPercentage = (charCount / MAX_CHARS) * 100;
  const charStatus = charPercentage >= 100 ? 'error' : charPercentage >= 90 ? 'warning' : 'normal';

  const handleCreateVersion = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await createPromptVersion(agentId, promptName, {
        content,
        versionNote: versionNote || undefined,
      });
      setSuccess(true);
      setShowVersionForm(false);
      setVersionNote('');
      onUpdate?.();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '建立版本失敗');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeploy = async () => {
    if (!currentPrompt) return;
    setIsDeploying(true);
    setError(null);
    try {
      await deployPromptVersion(agentId, promptName, currentPrompt.version);
      setSuccess(true);
      onUpdate?.();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '部署失敗');
    } finally {
      setIsDeploying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
          <div>
            <h3 className="font-display font-semibold text-lg">編輯: {promptName}</h3>
            {currentPrompt && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Badge variant="secondary">v{currentPrompt.version}</Badge>
                <span>更新於 {formatDate(currentPrompt.updatedAt)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            {showPreview ? '編輯' : '預覽'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-status-running/10 text-status-running text-sm">
          <Save className="h-4 w-4 flex-shrink-0" />
          <span>儲存成功</span>
        </div>
      )}

      {/* Editor */}
      <Card>
        <CardContent className="p-0">
          <div className="relative">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="輸入 Prompt 內容...支援 Markdown 語法"
              className={cn(
                "min-h-[400px] resize-none border-0 rounded-b-none focus-visible:ring-0",
                showPreview && 'hidden'
              )}
              maxLength={MAX_CHARS}
            />

            {showPreview && (
              <div className="min-h-[400px] p-6 prose prose-sm max-w-none dark:prose-invert">
                {content ? (
                  <div dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }} />
                ) : (
                  <p className="text-muted-foreground">預覽內容...</p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">字數:</span>
              <span className={cn(
                "text-sm font-medium",
                charStatus === 'error' && 'text-destructive',
                charStatus === 'warning' && 'text-status-paused'
              )}>
                {charCount} / {MAX_CHARS}
              </span>
              {charStatus === 'warning' && (
                <AlertTriangle className="h-4 w-4 text-status-paused" />
              )}
              {charStatus === 'error' && (
                <AlertTriangle className="h-4 w-4 text-destructive" />
              )}
            </div>

            <div className="flex items-center gap-2">
              {showVersionForm ? (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="版本說明（選填）"
                    value={versionNote}
                    onChange={(e) => setVersionNote(e.target.value)}
                    className="w-48 h-8"
                  />
                  <Button
                    size="sm"
                    onClick={handleCreateVersion}
                    disabled={isSaving || charCount === 0}
                  >
                    {isSaving ? '儲存中...' : '建立版本'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowVersionForm(false);
                      setVersionNote('');
                    }}
                  >
                    取消
                  </Button>
                </div>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowVersionForm(true)}
                  >
                    建立新版本
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleDeploy}
                    disabled={isDeploying}
                  >
                    {isDeploying ? '部署中...' : '部署'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Markdown Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Markdown 參考</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="font-medium mb-1">粗體</p>
              <code className="text-muted-foreground">**文字**</code>
            </div>
            <div>
              <p className="font-medium mb-1">斜體</p>
              <code className="text-muted-foreground">*文字*</code>
            </div>
            <div>
              <p className="font-medium mb-1">標題</p>
              <code className="text-muted-foreground">## 標題</code>
            </div>
            <div>
              <p className="font-medium mb-1">程式碼</p>
              <code className="text-muted-foreground">`code`</code>
            </div>
            <div>
              <p className="font-medium mb-1">列表</p>
              <code className="text-muted-foreground">- 項目</code>
            </div>
            <div>
              <p className="font-medium mb-1">引用</p>
              <code className="text-muted-foreground">&gt; 文字</code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Simple markdown renderer (basic implementation)
function renderMarkdown(text: string): string {
  return text
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/gim, '<em>$1</em>')
    // Code
    .replace(/`([^`]+)`/gim, '<code class="bg-muted px-1 py-0.5 rounded text-sm">$1</code>')
    // Line breaks
    .replace(/\n/gim, '<br>');
}
